<template>
  <div
    class="login-page"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
  >
    <!-- ===== Background ===== -->
    <div class="bg-fixed">
      <div class="video-wrap" :style="parallaxStyle">
        <video
          ref="heroVideo1"
          poster="https://image.mux.com/7v29npNaxJSUxCSwoS5O0227a8XcxXFCmBUhI4Y8ur5M/thumbnail.jpg"
          class="bg-video v1"
          preload="auto"
          autoplay
          muted
          playsinline
        ></video>
        <video
          ref="heroVideo2"
          class="bg-video v2"
          preload="auto"
          autoplay
          muted
          playsinline
        ></video>
      </div>
      <div class="blur-mask"></div>
      <div class="dot-bg" :style="dotBgStyle"></div>
    </div>

    <!-- ===== Toast ===== -->
    <transition name="toast-anim">
      <div v-show="toast.show" class="toast-wrap">
        <div class="toast-body">
          <i data-lucide="check-circle" class="toast-ic"></i>
          <span>{{ toast.msg }}</span>
        </div>
      </div>
    </transition>

    <!-- ===== Main ===== -->
    <main class="page-main">
      <!-- Logo Top -->
      <!-- <div class="logo-top">
        <img v-if="logoimg" :src="logoimg" alt="Logo" class="logo-img" />
      </div> -->

      <!-- Header -->
      <header class="page-hd anim-slide-up" style="animation-delay: 0.1s">
        <div class="brand-row">
          <div class="brand-icon">
            <img v-if="logoimg" :src="logoimg" alt="Logo" />
          </div>
        </div>
        <h1 class="page-h1">{{ slogan || "智能链图分析判研系统" }}</h1>
        <p class="page-desc">
          {{
            slogan_describe ||
            "以链上数据及匿名身份溯源为核心，打造数智链图新型涉案平台"
          }}
        </p>
      </header>

      <!-- Card wrapper -->
      <div class="card-wrap anim-slide-up" style="animation-delay: 0.2s">
        <!-- Glass card -->
        <div class="glass-card">
          <div class="card-inner-grad"></div>
          <!-- Spinning glow border -->
          <div class="spin-brd-mask">
            <div class="spin-brd-inner"></div>
          </div>

          <login-auth-views
            :current-view="currentView"
            :login-mode="loginMode"
            :form="form"
            :errors="errors"
            :pwd-vis="pwdVis"
            :countdown="countdown"
            :is-phone-valid="isPhoneValid"
            @set-login-mode="setLoginMode"
            @clear-error="clearError"
            @switch-view="switchView"
            @send-sms-code="sendSmsCode"
            @update-form-field="updateFormField"
            @toggle-pwd-vis="togglePwdVis"
            @submit="handleMainAction"
            @open-help="isopenhelp"
          />
        </div>
        <!-- /glass-card -->

        <!-- Submit Button -->
        <button
          @click="handleMainAction"
          :disabled="!isMainBtnActive || loading"
          :aria-busy="loading ? 'true' : 'false'"
          :class="[
            'submit-btn',
            isMainBtnActive && !loading ? 'submit-btn--on' : 'submit-btn--off',
            loading && 'submit-btn--loading',
          ]"
        >
          <span v-show="loading" class="btn-spinner"></span>
          <span class="submit-txt">{{ btnText }}</span>
          <span v-show="!loading" class="submit-ic-wrap">
            <span v-show="currentView === 'login'" class="submit-ic-inner"
              ><i data-lucide="arrow-right"></i
            ></span>
            <span v-show="currentView !== 'login'" class="submit-ic-inner"
              ><i data-lucide="check"></i
            ></span>
          </span>
        </button>
      </div>
      <!-- /card-wrap -->

      <footer
        class="page-ft"
        v-html="configValue1 || '&copy; 2022-2026 图链 Graphchain 版权所有'"
      ></footer>
    </main>

    <!-- Google Binding Dialog -->
    <el-dialog
      class="auth-dark-dialog"
      custom-class="auth-dark-dialog__panel auth-dark-dialog__panel--bind"
      :visible.sync="newuserBool"
      width="600px"
      :before-close="handleClose1"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <binding
        :googleform="googleform"
        @handleClose1="handleClose1"
        @openGoogleCode="isopenhelp"
        @bindingSuccess="handleBindingSuccess"
      ></binding>
    </el-dialog>

    <!-- Google Code Help Dialog -->
    <el-dialog
      class="auth-dark-dialog"
      custom-class="auth-dark-dialog__panel auth-dark-dialog__panel--guide"
      :visible.sync="seeCodeBool"
      width="1000px"
      top="7vh"
      :before-close="handleClose3"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <codegoogle
        :googleform="googleform"
        :handleClose3="handleClose3"
      ></codegoogle>
    </el-dialog>
  </div>
</template>

<script>
import {
  Login,
  checkverCode,
  Register,
  gentCaptcha,
  browserLogin,
} from "@/aips/login";
import {
  phoneRegister,
  phoneSecurityCoderLogin,
  phoneFindPassword,
} from "@/aips/phone";
import LoginAuthViews from "./components/LoginAuthViews.vue";
import binding from "./binding.vue";
import codegoogle from "./codegoogle.vue";
import global from "@/components/Global.vue";
import { beginAuthAttempt } from "@/utils/request.js";
import {
  clearGoogleAuthPending,
  clearLocalStorageExceptUserGuides,
  clearSessionStorageExceptSiteConfig,
  consumeRedirectUrl,
  getAccessTokenFromAuthData,
  getGoogleAuthPendingView,
  isGoogleAuthPending,
  setNosugarSessionFlag,
  setGoogleAuthPending,
  storeAuthData,
} from "@/utils/storageHelper.js";
import {
  waitForNosugar,
  checkNosugarLogin,
  getNosugarAccessToken,
  triggerNosugarLogin,
  NOSUGAR_CONFIG,
} from "@/utils/nosugar.js";

const PHONE_RE = /^(?:(?:\+|00)86)?1[3-9]\d{9}$/;
const ASCII_RE = /^[\x20-\x7E]{8,16}$/;
const LETTER_RE = /[A-Za-z]/;
const NUMBER_RE = /\d/;
const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{}|\\:;"'<>,.?/~`]/;

export default {
  name: "Login", // eslint-disable-line vue/multi-word-component-names
  components: { LoginAuthViews, binding, codegoogle },

  data() {
    return {
      currentView: "login", // login | register | forgot | 2fa
      loginMode: "account", // account | phone
      loading: false,
      isNosugarLoginInProgress: false,
      toast: { show: false, msg: "" },
      pwdVis: {
        acc: false,
        reg1: false,
        reg2: false,
        fgt1: false,
        fgt2: false,
      },

      form: {
        accUsername: "",
        accPwd: "",
        phMobile: "",
        phCode: "",
        regUsername: "",
        regMobile: "",
        regCode: "",
        regPwd: "",
        regPwdConfirm: "",
        fgtMobile: "",
        fgtCode: "",
        fgtPwd: "",
        fgtPwdConfirm: "",
        twofaCode: "",
      },

      errors: {
        accUsername: false,
        accPwd: false,
        phMobile: false,
        phCode: false,
        regUsername: false,
        regMobile: false,
        regCode: false,
        regPwd: false,
        regPwdConfirm: false,
        fgtMobile: false,
        fgtCode: false,
        fgtPwd: false,
        fgtPwdConfirm: false,
        twofaCode: false,
      },

      countdown: {
        ph: { active: false, time: 60, timer: null },
        reg: { active: false, time: 60, timer: null },
        fgt: { active: false, time: 60, timer: null },
      },

      hasReqCode: { reg: false, fgt: false },

      // google auth
      googleform: {},
      newuserBool: false,
      seeCodeBool: false,

      // site config
      slogan: null,
      slogan_describe: null,
      configValue1: null,
      loginresource: [],
      demoSite: null,
      logoimg: null,

      // parallax
      mouseConfig: {
        x: typeof window !== "undefined" ? window.innerWidth / 2 : 760,
        y: typeof window !== "undefined" ? window.innerHeight / 2 : 400,
        center: 0.1,
      },
      parallaxConfig: { currentX: 0, currentY: 0, targetX: 0, targetY: 0 },
      parallaxRAF: null,

      // video
      videoState: { active: 1, isFading: false, fadeTime: 1.0 },
    };
  },

  computed: {
    btnText() {
      const loadingMap = {
        login: "登录中",
        register: "注册中",
        forgot: "重置中",
        "2fa": "验证中",
      };
      const map = {
        login: "登录系统",
        register: "立即注册",
        forgot: "确认重置",
        "2fa": "验证身份",
      };
      if (this.loading) return loadingMap[this.currentView] || "处理中";
      return map[this.currentView];
    },

    isMainBtnActive() {
      const { currentView, loginMode, form } = this;
      if (currentView === "login") {
        return loginMode === "account"
          ? !!(form.accUsername.trim() && form.accPwd.trim())
          : !!(form.phMobile.trim() && form.phCode.trim());
      }
      if (currentView === "register") {
        return !!(
          form.regUsername.trim() &&
          form.regMobile.trim() &&
          form.regCode.trim() &&
          form.regPwd.trim() &&
          form.regPwdConfirm.trim()
        );
      }
      if (currentView === "forgot") {
        return !!(
          form.fgtMobile.trim() &&
          form.fgtCode.trim() &&
          form.fgtPwd.trim() &&
          form.fgtPwdConfirm.trim()
        );
      }
      if (currentView === "2fa") return form.twofaCode.trim().length === 6;
      return false;
    },

    parallaxStyle() {
      return {
        transform: `translate(${-this.parallaxConfig.currentX}px, ${-this
          .parallaxConfig.currentY}px) scale(1.05)`,
      };
    },

    dotBgStyle() {
      return {
        "--mouse-x": `${this.mouseConfig.x}px`,
        "--mouse-y": `${this.mouseConfig.y}px`,
        "--mask-center": this.mouseConfig.center,
      };
    },
  },

  async created() {
    const hasToken = window.localStorage.getItem("t_k");
    const googleAuthPending = isGoogleAuthPending();

    if (hasToken && !googleAuthPending) {
      this.$router.replace("/home");
      return;
    }

    if (googleAuthPending) {
      if (getGoogleAuthPendingView() === "2fa") {
        this.currentView = "2fa";
      } else {
        this.abortPendingGoogleAuth();
      }
    } else {
      clearLocalStorageExceptUserGuides();
      clearSessionStorageExceptSiteConfig();
    }
    await this.waitForSiteConfig();
    global.getConfig();

    // 无糖浏览器自动登录（带轮询等待，最多等5秒）
    const nosugarReady = await waitForNosugar();
    if (nosugarReady) {
      await this.handleNosugarAutoLogin();
    }
  },

  mounted() {
    this.initVideo();
    this.startParallax();
    this.reloadIcons();
    setTimeout(() => {
      global.getConfigdata.forEach((item) => {
        if (item.action === "LOGIN") this.loginresource = item.configs;
      });
      this.logoimg = this.getConfigValue("login_logo");
      this.slogan = this.getConfigValue("slogan");
      this.slogan_describe = this.getConfigValue("slogan_describe");
      this.configValue1 = this.getConfigValue("copyright");
      this.demoSite = this.getConfigValue("demoSite");
    }, 1000);
  },

  beforeDestroy() {
    if (this.parallaxRAF) cancelAnimationFrame(this.parallaxRAF);
    Object.values(this.countdown).forEach((c) => {
      if (c.timer) clearInterval(c.timer);
    });
  },

  methods: {
    // ---- Navigation ----

    // ---- View ----
    switchView(view) {
      if (view === "2fa") {
        this.enterTwoFaView();
        return;
      }
      if (this.currentView === "2fa") {
        this.abortPendingGoogleAuth();
        this.resetTwoFaCode();
      }
      this.currentView = view;
      this.resetErrors();
      this.reloadIcons();
    },

    setLoginMode(mode) {
      this.loginMode = mode;
      this.reloadIcons();
    },

    clearError(field) {
      this.errors[field] = false;
    },
    updateFormField({ field, value }) {
      this.form[field] = value;
    },
    togglePwdVis(field) {
      this.pwdVis[field] = !this.pwdVis[field];
    },
    resetErrors() {
      Object.keys(this.errors).forEach((k) => {
        this.errors[k] = false;
      });
    },
    resetTwoFaCode() {
      this.form.twofaCode = "";
      this.errors.twofaCode = false;
    },
    enterTwoFaView() {
      this.resetTwoFaCode();
      this.currentView = "2fa";
      this.resetErrors();
      this.reloadIcons();
    },
    abortPendingGoogleAuth() {
      clearGoogleAuthPending();
      clearLocalStorageExceptUserGuides();
      clearSessionStorageExceptSiteConfig();
      this.googleform = {};
      this.newuserBool = false;
    },
    startPendingGoogleAuth(data, view) {
      if (localStorage.t_k) clearLocalStorageExceptUserGuides();
      if (!storeAuthData(data)) return false;
      setGoogleAuthPending(view);
      return true;
    },
    finishLogin(data) {
      beginAuthAttempt();
      if (localStorage.t_k) clearLocalStorageExceptUserGuides();
      clearGoogleAuthPending();
      if (!storeAuthData(data)) {
        this.$tbMessage({
          type: "error",
          text: this.$t("login.loginFailed") || "登录失败",
        });
        return;
      }
      setNosugarSessionFlag(!!this.isNosugarLoginInProgress);
      this.redirectAfterLogin();
    },
    redirectAfterLogin() {
      const redirectUrl = consumeRedirectUrl();
      if (redirectUrl) {
        this.$router.push(redirectUrl);
      } else {
        this.demoSite ? this.$router.push("/") : this.$router.push("/home");
      }
    },

    isPhoneValid(phone) {
      return PHONE_RE.test(phone);
    },
    isPasswordValid(pwd) {
      return (
        ASCII_RE.test(pwd) &&
        LETTER_RE.test(pwd) &&
        NUMBER_RE.test(pwd) &&
        SPECIAL_RE.test(pwd)
      );
    },

    // ---- Toast ----
    showToastMsg(msg) {
      this.toast.msg = msg;
      this.toast.show = true;
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    // ---- SMS countdown ----
    startCountdown(type) {
      const c = this.countdown[type];
      if (c.active) return;
      c.active = true;
      c.time = 60;
      c.timer = setInterval(() => {
        c.time--;
        if (c.time <= 0) {
          clearInterval(c.timer);
          c.timer = null;
          c.active = false;
        }
      }, 1000);
    },

    async sendSmsCode(type) {
      if (this.countdown[type].active) return;
      try {
        let resp;
        if (type === "ph") {
          resp = await phoneSecurityCoderLogin({
            phone: this.form.phMobile,
            operationType: "LOGIN",
          });
        } else if (type === "reg") {
          if (!this.form.regUsername) {
            this.errors.regUsername = true;
            return;
          }
          resp = await gentCaptcha({
            account: this.form.regUsername,
            phone: this.form.regMobile,
          });
        } else if (type === "fgt") {
          resp = await phoneSecurityCoderLogin({
            phone: this.form.fgtMobile,
            operationType: "CHANGE_PASSWORD",
          });
        }
        if (resp && resp.code === 200) {
          this.showToastMsg("验证码已发送");
          if (type !== "ph") this.hasReqCode[type] = true;
          this.startCountdown(type);
        }
      } catch (e) {
        console.error("发送验证码失败", e);
      }
    },

    // ---- Main action ----
    async handleMainAction() {
      if (!this.isMainBtnActive || this.loading) return;
      let valid = true;

      if (this.currentView === "login") {
        if (this.loginMode === "account") {
          if (!this.form.accUsername) {
            this.errors.accUsername = true;
            valid = false;
          }
          if (!this.form.accPwd) {
            this.errors.accPwd = true;
            valid = false;
          }
        } else {
          if (!this.isPhoneValid(this.form.phMobile)) {
            this.errors.phMobile = true;
            valid = false;
          }
          if (!this.form.phCode) {
            this.errors.phCode = true;
            valid = false;
          }
        }
      } else if (this.currentView === "register") {
        if (!this.form.regUsername) {
          this.errors.regUsername = true;
          valid = false;
        }
        if (!this.isPhoneValid(this.form.regMobile)) {
          this.errors.regMobile = true;
          valid = false;
        }
        if (!this.form.regCode) {
          this.errors.regCode = true;
          valid = false;
        }
        if (!this.isPasswordValid(this.form.regPwd)) {
          this.errors.regPwd = true;
          valid = false;
        }
        if (this.form.regPwd !== this.form.regPwdConfirm) {
          this.errors.regPwdConfirm = true;
          valid = false;
        }
      } else if (this.currentView === "forgot") {
        if (!this.isPhoneValid(this.form.fgtMobile)) {
          this.errors.fgtMobile = true;
          valid = false;
        }
        if (!this.form.fgtCode) {
          this.errors.fgtCode = true;
          valid = false;
        }
        if (!this.isPasswordValid(this.form.fgtPwd)) {
          this.errors.fgtPwd = true;
          valid = false;
        }
        if (this.form.fgtPwd !== this.form.fgtPwdConfirm) {
          this.errors.fgtPwdConfirm = true;
          valid = false;
        }
      } else if (this.currentView === "2fa") {
        if (this.form.twofaCode.length !== 6) {
          this.errors.twofaCode = true;
          valid = false;
        }
      }

      if (!valid) return;

      this.loading = true;
      try {
        if (this.currentView === "login") await this.doLogin();
        else if (this.currentView === "register") await this.doRegister();
        else if (this.currentView === "forgot") await this.doForgot();
        else if (this.currentView === "2fa") await this.doTwoFa();
      } finally {
        this.loading = false;
      }
    },

    async doLogin() {
      try {
        // 新的账号认证从干净状态开始，使旧页面尚未返回的 401 立即失效。
        beginAuthAttempt();
        clearGoogleAuthPending();
        clearLocalStorageExceptUserGuides();
        clearSessionStorageExceptSiteConfig();

        let res;
        if (this.loginMode === "account") {
          res = await Login({
            username: this.form.accUsername,
            password: this.form.accPwd,
          });
        } else {
          res = await phoneRegister({
            username: this.form.phMobile,
            password: this.form.phCode,
          });
        }
        if (res.code === 300) return;
        this.handleLoginSuccess(res.data);
      } catch (e) {
        this.form.accPwd = "";
        this.form.phCode = "";
        this.$tbMessage({
          type: "error",
          text:
            e.message ||
            this.$t("login.loginFailed") ||
            "登录失败，请检查账号密码",
        });
      }
    },

    async doRegister() {
      if (!this.hasReqCode.reg) {
        this.showToastMsg("请先获取验证码");
        return;
      }
      try {
        const res = await Register({
          account: this.form.regUsername,
          phone: this.form.regMobile,
          captcha: this.form.regCode,
          password: this.form.regPwd,
        });
        if (res.code === 200) {
          this.showToastMsg("注册成功，请登录");
          this.switchView("login");
          this.hasReqCode.reg = false;
          // 清空注册表单
          this.form.regUsername = "";
          this.form.regMobile = "";
          this.form.regCode = "";
          this.form.regPwd = "";
          this.form.regPwdConfirm = "";
        }
      } catch (e) {
        this.form.regPwd = "";
        this.form.regPwdConfirm = "";
        this.hasReqCode.reg = false;
        this.$tbMessage({
          type: "error",
          text:
            e.message || this.$t("login.registerFailed") || "注册失败，请重试",
        });
      }
    },

    async doForgot() {
      if (!this.hasReqCode.fgt) {
        this.showToastMsg("请先获取验证码");
        return;
      }
      try {
        const res = await phoneFindPassword({
          phone: this.form.fgtMobile,
          authCode: this.form.fgtCode,
          password: this.form.fgtPwd,
          type: "PHONE",
        });
        if (res && (res.code === 200 || res.status === 200)) {
          this.showToastMsg("密码重置成功，请登录");
          this.switchView("login");
          this.hasReqCode.fgt = false;
          // 清空找回密码表单
          this.form.fgtMobile = "";
          this.form.fgtCode = "";
          this.form.fgtPwd = "";
          this.form.fgtPwdConfirm = "";
        }
      } catch (e) {
        this.form.fgtPwd = "";
        this.form.fgtPwdConfirm = "";
        this.$tbMessage({
          type: "error",
          text:
            e.message ||
            this.$t("login.resetPasswordFailed") ||
            "密码重置失败，请重试",
        });
      }
    },

    async doTwoFa() {
      try {
        if (this.displaynotify) this.displaynotify.isShow = true;
        const authToken =
          getAccessTokenFromAuthData(this.googleform) ||
          window.localStorage.getItem("t_k") ||
          "";
        const res = await checkverCode(this.form.twofaCode, authToken);
        if (res.code === 200) {
          this.$tbMessage({
            text: this.$t("login.loginSuccess"),
            type: "success",
            duration: 1000,
          });
          this.finishLogin(res.data);
        }
      } catch (e) {
        this.errors.twofaCode = true;
        this.$tbMessage({ type: "error", text: this.$t("登录失败") });
        setTimeout(() => {
          this.switchView("login");
          clearLocalStorageExceptUserGuides();
        }, 1500);
      }
    },

    handleLoginSuccess(data) {
      const bindingFlag = Number(data && data.bindingFlag);

      if (bindingFlag === 0) {
        if (!this.startPendingGoogleAuth(data, "binding")) return;
        this.newuserBool = true;
        this.googleform = data;
      } else if (bindingFlag === 1) {
        if (!this.startPendingGoogleAuth(data, "2fa")) return;
        this.googleform = data;
        this.enterTwoFaView();
      } else {
        this.finishLogin(data);
      }
    },

    handleBindingSuccess() {
      this.newuserBool = false;
      setGoogleAuthPending("2fa");
      this.enterTwoFaView();
    },

    handleClose1() {
      this.newuserBool = false;
      this.abortPendingGoogleAuth();
    },
    handleClose3() {
      this.seeCodeBool = false;
    },
    isopenhelp() {
      this.seeCodeBool = true;
    },

    // ---- 无糖浏览器自动登录 ----
    async handleNosugarAutoLogin() {
      const TAG = "[无糖]";
      console.log(TAG, "========== 开始无糖自动登录流程 ==========");
      this.loading = true;
      try {
        // 1. 检查浏览器用户是否已登录
        const isLogined = await checkNosugarLogin();

        if (!isLogined) {
          // 2. 未登录 → 唤起浏览器登录窗口
          console.log(TAG, "4. 用户未登录，唤起浏览器登录窗口...");
          await triggerNosugarLogin();
          // 登录完成后再次检查
          console.log(TAG, "   登录窗口已关闭，重新检查登录状态...");
          const recheckLogin = await checkNosugarLogin();
          if (!recheckLogin) {
            console.log(TAG, "   ⚠️ 用户取消了登录，显示普通登录表单");
            this.loading = false;
            return;
          }
        }

        // 3. 获取 access_token → 调用后端接口换取图链 JWT
        const accessToken = await getNosugarAccessToken(NOSUGAR_CONFIG.APP_ID);

        console.log(TAG, "6. 调用后端接口 POST /v2/user/browser/login...");
        console.log(TAG, "   请求参数:", {
          access_token: accessToken.substring(0, 20) + "...",
        });
        const res = await browserLogin({
          access_token: accessToken,
        });

        console.log(TAG, "7. 后端接口返回:");
        console.log(TAG, "   code:", res.code);
        console.log(TAG, "   data:", res.data);

        // 4. 无糖浏览器用户跳过 Google Auth，直接完成登录
        if (res.code === 200) {
          console.log(
            TAG,
            "   ✅ 认证成功无糖用户跳过GoogleAuth，直接进入应用",
          );
          this.isNosugarLoginInProgress = true;
          setNosugarSessionFlag(true);
          this.finishLogin(res.data);
          this.isNosugarLoginInProgress = false;
        } else {
          console.log(
            TAG,
            "   ❌ 认证失败，code:",
            res.code,
            "message:",
            res.message,
          );
          this.$tbMessage({
            type: "warning",
            text: "自动登录失败，请手动登录",
          });
          this.loading = false;
        }
      } catch (e) {
        console.error(TAG, "❌ 无糖自动登录异常:", e);
        console.error(TAG, "   错误详情:", e.message || e);
        this.loading = false;
        // 静默降级：用户看到普通登录表单，可手动输入账号密码登录
      }
    },

    // ---- Icons ----
    reloadIcons() {
      this.$nextTick(() => {
        if (window.lucide)
          window.lucide.createIcons({ attrs: { "stroke-width": "1.8" } });
      });
    },

    // ---- Mouse parallax ----
    handleMouseMove(e) {
      this.parallaxConfig.targetX = (e.clientX / window.innerWidth - 0.5) * 40;
      this.parallaxConfig.targetY = (e.clientY / window.innerHeight - 0.5) * 40;
      this.mouseConfig.x = e.clientX;
      this.mouseConfig.y = e.clientY;
      this.mouseConfig.center = 0.28;
    },
    handleMouseLeave() {
      this.mouseConfig.center = 0.1;
    },

    startParallax() {
      const animate = () => {
        this.parallaxConfig.currentX +=
          (this.parallaxConfig.targetX - this.parallaxConfig.currentX) * 0.05;
        this.parallaxConfig.currentY +=
          (this.parallaxConfig.targetY - this.parallaxConfig.currentY) * 0.05;
        this.parallaxRAF = requestAnimationFrame(animate);
      };
      animate();
    },

    // ---- Video background ----
    initVideo() {
      const v1 = this.$refs.heroVideo1;
      const v2 = this.$refs.heroVideo2;
      const src =
        "https://stream.mux.com/7v29npNaxJSUxCSwoS5O0227a8XcxXFCmBUhI4Y8ur5M.m3u8";

      const setupVideo = (video, isFirst) => {
        video.playbackRate = 0.8;
        const HlsLib = window.Hls;
        if (HlsLib && HlsLib.isSupported()) {
          const hls = new HlsLib({ maxBufferLength: 30 });
          hls.loadSource(src);
          hls.attachMedia(video);
          if (isFirst)
            hls.on(HlsLib.Events.MANIFEST_PARSED, () =>
              video.play().catch(() => {}),
            );
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = src;
          if (isFirst)
            video.addEventListener("loadedmetadata", () =>
              video.play().catch(() => {}),
            );
        }
        video.addEventListener("timeupdate", () => {
          if (!video.duration || this.videoState.isFading) return;
          const vs = this.videoState;
          if (
            vs.active === 1 &&
            video === v1 &&
            v1.currentTime >= v1.duration - vs.fadeTime
          ) {
            vs.isFading = true;
            this.crossfadeTo(v2, v1);
          } else if (
            vs.active === 2 &&
            video === v2 &&
            v2.currentTime >= v2.duration - vs.fadeTime
          ) {
            vs.isFading = true;
            this.crossfadeTo(v1, v2);
          }
        });
      };

      setupVideo(v1, true);
      setupVideo(v2, false);
    },

    crossfadeTo(next, current) {
      this.videoState.active = next === this.$refs.heroVideo1 ? 1 : 2;
      next.currentTime = 0;
      const p = next.play();
      if (p !== undefined) {
        p.then(() => {
          next.style.opacity = "1";
          current.style.opacity = "0";
          setTimeout(() => {
            current.pause();
            this.videoState.isFading = false;
          }, this.videoState.fadeTime * 1000);
        }).catch(() => {
          this.videoState.isFading = false;
        });
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "@/assets/css/newLogin/loginStyle.less";
</style>

<style lang="less">
@import "@/assets/css/newLogin/authDarkDialog.less";
</style>
